// src/pages/Help.js
// vHELP-003
//
// Helptekst StaffIQ.
// Opzet: kort waar het vanzelf spreekt, uitgebreider waar het proces extra uitleg vraagt.

import React from "react";

export default function Help() {
  const pageStyle = {
    maxWidth: 980,
    lineHeight: 1.55,
  };

  const sectionStyle = {
    marginTop: 22,
  };

  const smallStyle = {
    color: "#555",
  };

  return (
    <div style={pageStyle}>
      <h1>Help</h1>

      <p>
        StaffIQ is een praktisch CRM/HRM-systeem voor het MKB, personele
        dienstverlening en projectmatig werkende bedrijven. Het systeem helpt bij
        relaties, taken, documenten, offertes, opdrachten, facturen, personeel,
        matching, aanvragen en plaatsingen.
      </p>

      <p>
        De functies hebben zoveel mogelijk dezelfde opzet. Daardoor werkt een
        nieuwe functie meestal op dezelfde manier als een bekende functie.
      </p>

      <h2 style={sectionStyle}>Basisgebruik</h2>

      <p>
        Op laptop en pc staat links het menu, in het midden het overzicht en
        rechts het invoerblok. Klik op een regel in het overzicht om de gegevens
        rechts te openen. Op smartphone staan overzicht en invoerblok onder
        elkaar. Het menu open je met de drie streepjes linksboven.
      </p>

      <p>
        In de overzichten kun je zoeken, sorteren en filteren. Telefoonnummers,
        mobiele nummers en e-mailadressen zijn aanklikbaar. Bij een mobiel
        nummer kun je kiezen voor bellen, SMS of WhatsApp.
      </p>

      <p>
        De knop <b>Spreek</b> kan gebruikt worden als de browser
        spraakondersteuning heeft. Voorbeelden van spraakcommando&apos;s zijn:
        volgende, vorige, nieuw, vernieuw, item, selecteer, rechts, links, leeg,
        punt, open, klaar, ja, nee, aan en uit.
      </p>

      <h2 style={sectionStyle}>CRM voor het MKB</h2>

      <h3>Relaties en contactpersonen</h3>

      <p>
        In Relaties leg je organisaties en particulieren vast. Contactpersonen
        horen bij een relatie. De gegevens uit Relaties worden zoveel mogelijk
        automatisch gebruikt bij offertes, opdrachten, facturen, documenten en
        communicatie.
      </p>

      <p>
        Bij een relatie kun je instellen of correspondentie en digitaal akkoord
        via WhatsApp en/of e-mail mag verlopen. Deze instellingen bepalen of
        akkoordverzoeken beschikbaar zijn bij offertes, opdrachten en
        plaatsingsbevestigingen voor de klant. Voor klantcommunicatie gebruikt
        StaffIQ de gegevens van de relatie zelf.
      </p>

      <h3>Meerdere bedrijfscodes</h3>

      <p>
        StaffIQ kan met meerdere bedrijfscodes werken. Hiermee kun je gegevens
        scheiden per administratie, vestiging, klantgroep of werkmaatschappij.
        Gebruikers zien alleen de bedrijfscodes waarvoor zij rechten hebben.
        De administrator beheert de bedrijfscodes en de rechten per gebruiker.
      </p>

      <p>
        Bij het aanmaken van nieuwe gegevens kiest StaffIQ zoveel mogelijk zelf
        de juiste bedrijfscode. Controleer dit vooral bij gegevens die met meerdere
        onderdelen tegelijk te maken hebben, zoals taken, documenten en
        plaatsingen.
      </p>

      <h3>Notities en taken</h3>

      <p>
        Notities en taken staan in één functie. Een notitie is vooral informatie.
        Een taak heeft een status, uitvoerder, vervaldatum en eventueel een
        herinnering.
      </p>

      <p>
        Bij het inloggen toont StaffIQ openstaande taken. Als een herinnering is
        ingesteld, kan de uitvoerder een e-mailherinnering krijgen zolang de taak
        nog niet op <b>Klaar</b> staat.
      </p>

      <p>
        Wanneer een medewerker een taak op <b>Klaar</b> zet, kan de aanmaker van
        de taak automatisch een e-mailmelding ontvangen. Zo blijft zichtbaar dat
        een uitgezette actie is afgerond.
      </p>

      <h3>Documentbeheer</h3>

      <p>
        Documenten worden opgeslagen in een vaste mapstructuur per relatie of
        personeelslid. De basismap wordt ingesteld bij Instellingen. StaffIQ maakt
        de benodigde mappen automatisch aan.
      </p>

      <p>
        Ook door StaffIQ gemaakte documenten, zoals offertes, opdrachten,
        facturen, personeelskaarten, matchingdocumenten en
        plaatsingsbevestigingen, worden automatisch in de juiste map geplaatst.
      </p>

      <h3>Projectbeheer</h3>

      <p>
        In Projectbeheer kun je projecten en deelprojecten in niveaus vastleggen,
        bijvoorbeeld 01, 01.01 en 01.02. Per onderdeel kun je tekst en eventueel
        een prijs vastleggen. Deze gegevens kunnen worden gebruikt in offertes en
        opdrachten.
      </p>

      <h3>Offertes en opdrachten</h3>

      <p>
        In Offertes / Opdrachten maak je offertes met artikelregels, tekstregels,
        kopregels, subtotalen en totalen. Een goedgekeurde offerte kan worden
        omgezet naar een opdracht. Vanuit een opdracht kan later een conceptfactuur
        worden aangemaakt.
      </p>

      <p>
        Een offerte of opdracht kan worden geprint, opgeslagen als document of
        per e-mail worden verstuurd. Via <b>Ter akkoord</b> kan een digitaal
        akkoordverzoek worden gemaakt.
      </p>

      <p>
        Bij digitaal akkoord kiest StaffIQ op basis van de relatie-instellingen
        voor WhatsApp, e-mail of beide. De ontvanger krijgt een akkoordlink,
        kan de PDF bekijken en kan digitaal akkoord of niet akkoord geven.
        De gebruikte teksten voor WhatsApp en e-mail zijn instelbaar bij
        <b> Instellingen &gt; Digitale accordering</b>.
      </p>

      <h3>Facturen</h3>

      <p>
        In Facturen controleer je conceptfacturen en maak je deze definitief.
        Daarna krijgt de factuur een factuurnummer en kan deze worden geprint of
        per e-mail worden verstuurd. Deelbetalingen kunnen in het
        facturenprogramma worden bijgehouden.
      </p>

      <h3>Drie standaardtarieven per klant</h3>

      <p>
        Bij een relatie kunnen drie standaardtarieven worden vastgelegd,
        bijvoorbeeld junior, medior en senior. Deze tarieven kunnen later worden
        gebruikt als basis voor offertes, aanvragen, plaatsingen of andere
        prijsafspraken. Het blijven standaardwaarden: je kunt ze waar nodig per
        document of plaatsing aanpassen.
      </p>

      <h2 style={sectionStyle}>HRM voor personele dienstverlening</h2>

      <h3>Personeel</h3>

      <p>
        In Personeel leg je kandidaten en medewerkers vast. De personeelskaart
        kan worden geprint, als document worden opgeslagen en worden gebruikt
        bij matching.
      </p>

      <p>
        Bij een personeelslid kun je instellen of correspondentie en digitaal
        akkoord via WhatsApp en/of e-mail mag verlopen. Deze instellingen worden
        gebruikt bij akkoordverzoeken voor de kandidaat, bijvoorbeeld bij een
        plaatsingsbevestiging kandidaat.
      </p>

      <h3>CV importeren met AI</h3>

      <p>
        Bij het aanmaken van een nieuw personeelslid kun je een CV gebruiken om
        alvast gegevens te laten invullen. Kies bij <b>Personeel</b> eerst
        <b> + Nieuw</b>. Bovenin de personeelskaart staan de knoppen
        <b> CV importeren</b> en <b> Foto maken</b>.
      </p>

      <p>
        Met <b>CV importeren</b> kun je een PDF, Word-bestand of tekstbestand
        kiezen. Met <b>Foto maken</b> kun je op een smartphone direct een foto
        van een papieren CV maken, of op een pc een afbeelding kiezen. StaffIQ
        gebruikt AI om de inhoud te lezen en vult daarna zoveel mogelijk velden
        alvast in.
      </p>

      <p>
        De AI kan onder andere naam, contactgegevens, adres, functie, opleiding,
        werkervaring, certificaten, talen, rijbewijs en een korte samenvatting
        herkennen. De gegevens worden <b>niet automatisch opgeslagen</b>.
        Controleer en verbeter de velden eerst zelf en klik daarna pas op
        <b> Opslaan</b>.
      </p>

      <p>
        Een pasfoto uit een CV wordt nog niet automatisch overgenomen. Je kunt
        wel alvast een pasfoto kiezen voordat het personeelsrecord is opgeslagen.
        StaffIQ zet deze foto tijdelijk klaar en koppelt deze automatisch na het
        opslaan van de medewerker.
      </p>

      <h3>Matching</h3>

      <p>
        StaffIQ heeft matching voor personeel en relaties. Matching is een
        selectiehulpmiddel. Het systeem toont passende resultaten op basis van
        criteria zoals functie, regio, woonplaats, vervoer, rijbewijs, tarief,
        uurloon en profieltekst.
      </p>

      <h3>Aanvragen</h3>

      <p>
        In Aanvragen leg je de klantvraag vast: relatie, contactpersoon, functie,
        aantal personen, startdatum, uren per week, locatie, tarief, factor,
        reiskosten, eisen en opmerkingen.
      </p>

      <p>
        Als <b>Factor</b> en <b>Salarisindicatie</b> zijn ingevuld, kan StaffIQ
        het <b>Verkooptarief</b> automatisch berekenen als factor x
        salarisindicatie. Het verkooptarief blijft altijd handmatig aanpasbaar.
        Als factor of salarisindicatie leeg is, laat StaffIQ het verkooptarief
        ongemoeid.
      </p>

      <p>
        Vanuit een aanvraag kun je direct naar <b>Match Personeel</b>. StaffIQ
        neemt de belangrijkste criteria mee en toont geschikte kandidaten. Na het
        kiezen van een kandidaat kan direct een conceptplaatsing worden gemaakt.
      </p>

      <h3>Plaatsingen</h3>

      <p>
        Een plaatsing is de concrete combinatie van klant, aanvraag en kandidaat.
        De plaatsing begint meestal als <b>Concept</b>. Daarna kunnen kandidaat
        en klant akkoord geven, waarna de plaatsing kan worden bevestigd en later
        gestart.
      </p>

      <p>
        De globale proceslijn is:
      </p>

      <ol>
        <li><b>Aanvraag aanmaken.</b> Leg klantvraag, functie, periode, uren en tariefafspraken vast.</li>
        <li><b>Match Personeel openen.</b> StaffIQ neemt criteria uit de aanvraag mee naar matching.</li>
        <li><b>Kandidaat kiezen.</b> Vanuit matching wordt een conceptplaatsing aangemaakt.</li>
        <li><b>Plaatsing controleren.</b> Controleer klant, kandidaat, functie, periode, uren, loon- en factuurafspraken.</li>
        <li><b>Kandidaat akkoord.</b> Leg akkoord van de kandidaat vast of stuur een digitale plaatsingsbevestiging kandidaat.</li>
        <li><b>Klant akkoord.</b> Leg akkoord van de klant vast of stuur een digitale plaatsingsbevestiging klant.</li>
        <li><b>Plaatsing bevestigen.</b> Dit kan pas logisch wanneer kandidaat en klant akkoord zijn.</li>
        <li><b>Plaatsing starten.</b> Gebruik dit wanneer de kandidaat daadwerkelijk begint.</li>
        <li><b>Plaatsing stoppen of annuleren.</b> Gebruik dit bij beëindiging of wanneer de plaatsing niet doorgaat.</li>
      </ol>

      <p>
        De vinkjes <b>Kandidaat bevestigd</b> en <b>Klant bevestigd</b> sturen
        de kandidaat- en klantstatus mee. Als een vinkje weer wordt uitgezet,
        zet StaffIQ de betreffende status ook terug. Daardoor blijft het proces
        beter in lijn met de werkelijke situatie.
      </p>

      <h3>Plaatsingsbevestigingen</h3>

      <p>
        Bij Plaatsingen kun je aparte documenten maken voor klant en kandidaat.
        Gebruik <b>Voorbeeld klant</b> of <b>Voorbeeld kand</b> om de inhoud te
        controleren. Met <b>Opslaan klant</b> en <b>Opslaan kand</b> wordt de
        bevestiging opgeslagen bij Documenten.
      </p>

      <p>
        Met <b>Mail klant</b> en <b>Mail kand</b> verstuur je de bevestiging per
        e-mail. StaffIQ toont daarna of de mailserver het bericht heeft
        geaccepteerd.
      </p>

      <p>
        Met <b>Akkoord klant</b> en <b>Kandidaat akk</b> maak je een digitaal
        akkoordverzoek. StaffIQ kijkt automatisch naar de communicatievinkjes bij
        de relatie of de kandidaat. Als WhatsApp en e-mail beide zijn toegestaan,
        kun je kiezen voor WhatsApp, e-mail of beide. Voor klant en kandidaat
        worden aparte documenten, teksten en ontvangers gebruikt.
      </p>

      <p>
        De ontvanger opent de akkoordpagina, bekijkt de PDF en vult naam,
        e-mailadres en eventueel een opmerking in. Bij klantakkoord moet de
        ontvanger bevestigen dat hij of zij bevoegd is namens de organisatie.
        Bij kandidaatakkoord bevestigt de kandidaat akkoord met het document.
      </p>

      <p>
        Een bevestigde plaatsing is administratief rond. Een gestarte plaatsing
        is operationeel begonnen. Dit wordt later belangrijk voor planning,
        bezetting, urenregistratie, facturering en managementinformatie.
      </p>

      <h2 style={sectionStyle}>Instellingen en rechten</h2>

      <p>
        Alleen de administrator kan instellingen aanpassen. Denk hierbij aan
        documentmappen, factuur- en offerteteksten, communicatie-instellingen,
        digitale akkoordteksten, gebruikers, rechten en bedrijfscodes.
      </p>

      <p>
        Bij <b>Instellingen &gt; Digitale accordering</b> beheer je de teksten
        voor WhatsApp, e-mail en akkoordbevestigingen. Er zijn aparte teksten
        voor offertes/opdrachten en voor plaatsingsbevestigingen klant en
        kandidaat.
      </p>

      <h2 style={sectionStyle}>In ontwikkeling</h2>

      <p>
        StaffIQ wordt verder uitgebreid. Onderdelen die in ontwikkeling zijn of
        later worden toegevoegd zijn onder andere:
      </p>

      <ul>
        <li>Urenregistratie</li>
        <li>Planning</li>
        <li>Bezettingsoverzicht</li>
        <li>Materiaal / producten / stuklijst</li>
        <li>Inkoop</li>
        <li>Magazijn / voorraad</li>
        <li>Kostprijsberekening en voorcalculatie</li>
        <li>Pakbonnen</li>
        <li>Nacalculatie</li>
        <li>Managementinformatie en dashboard</li>
      </ul>

      <p style={smallStyle}>
        StaffIQ blijft in ontwikkeling. Suggesties zijn welkom via
        info@staffiq.nl of via www.staffiq.nl.
      </p>
    </div>
  );
}
